// ctl-thread.js
// <ctl-thread> — message timeline for a single conversation/thread
// - No dependencies
// - Supports template override via <template slot="message"> with {{placeholders}}
// - Slots: header, message (template), footer, empty, error
// - Props: messages (array), conversation (object)
// - Attrs: loading, error, typing, can-load-more, persona, channel
// - Events: ctl-load-more, ctl-message-action, ctl-link-click
//
// Template placeholders available in slot="message" template:
// {{id}}, {{channel}}, {{direction}}, {{kind}}, {{createdAt}}
// {{authorName}}, {{authorType}}
// {{subject}}, {{body}}, {{bodyText}}
// {{status}}

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-radius: var(--ctl-radius-lg, 14px);
    --ctl-radius-sm: var(--ctl-radius-md, 10px);
    --ctl-shadow: var(--ctl-shadow-sm, 0 6px 18px rgba(0,0,0,.10));

    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .frame{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius);
    box-shadow: var(--ctl-shadow);
    overflow: hidden;
  }

  .header{
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }
  .header:empty{ display:none; }

  .body{
    position: relative;
    background: var(--ctl-color-surface);
  }

  .scroller{
    max-height: 520px;
    overflow: auto;
    padding: var(--ctl-space-3-5);
    scroll-behavior: smooth;
  }

  .topControls{
    display:flex;
    align-items:center;
    justify-content:center;
    padding: var(--ctl-space-2-5) var(--ctl-space-3-5) 0;
  }

  .loadMoreBtn{
    all: unset;
    cursor: pointer;
    font-weight: var(--ctl-font-weight-extrabold);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
    padding: var(--ctl-space-2) var(--ctl-space-2-5);
    border-radius: var(--ctl-radius-10);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
  }
  .loadMoreBtn:hover{ filter: brightness(.98); }
  .loadMoreBtn:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }

  .timeline{
    list-style:none;
    padding: 0;
    margin: 0;
    display:flex;
    flex-direction: column;
    gap: var(--ctl-space-2-5);
  }

  /* Message rows */
  .row{
    display:flex;
    align-items:flex-end;
    gap: var(--ctl-space-2-5);
  }
  .row[data-dir="out"]{ justify-content: flex-end; }

  .meta{
    display:flex;
    gap: var(--ctl-space-2);
    align-items:center;
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-muted);
    user-select:none;
  }
  .meta .pill{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
    padding: var(--ctl-space-0-5) 7px;
    border-radius: var(--ctl-radius-full);
    font-weight: var(--ctl-font-weight-extrabold);
    font-size: var(--ctl-font-size-2xs);
    letter-spacing: .2px;
  }

  .bubble{
    max-width: min(560px, 92%);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
    border-radius: var(--ctl-radius-14);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
    position: relative;
  }

  .row[data-dir="out"] .bubble{
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, var(--ctl-color-surface));
    border-color: color-mix(in srgb, var(--ctl-color-brand) 22%, var(--ctl-color-border));
  }

  .author{
    font-weight: var(--ctl-font-weight-black);
    font-size: var(--ctl-font-size-sm);
    letter-spacing: .2px;
    margin: 0 0 var(--ctl-space-1-5);
  }
  .row[data-dir="out"] .author{ text-align:right; }

  .content{
    font-size: var(--ctl-font-size-md);
    line-height: 1.35;
    word-break: break-word;
    white-space: pre-wrap;
  }

  .content a{
    color: var(--ctl-color-brand);
    text-decoration: underline;
  }

  .footerRow{
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
    margin-top: var(--ctl-space-2);
  }
  .status{
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-heavy);
  }

  .msgActions{
    display:flex;
    gap: var(--ctl-space-1-5);
  }
  .iconBtn{
    all: unset;
    cursor: pointer;
    border-radius: var(--ctl-radius-10);
    padding: var(--ctl-space-1-5) var(--ctl-space-2);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-black);
    color: var(--ctl-color-muted);
  }
  .iconBtn:hover{ filter: brightness(.98); }
  .iconBtn:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }

  /* System / note variants */
  .row[data-kind="system"] .bubble,
  .row[data-kind="event"] .bubble{
    max-width: 100%;
    width: 100%;
    background: var(--ctl-color-surface);
  }

  .row[data-kind="note"] .bubble{
    background: color-mix(in srgb, #f59e0b 10%, var(--ctl-color-surface));
    border-color: color-mix(in srgb, #f59e0b 25%, var(--ctl-color-border));
  }

  /* Typing indicator */
  .typing{
    display:flex;
    gap: var(--ctl-space-1-5);
    align-items:center;
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-heavy);
    font-size: var(--ctl-font-size-sm);
    padding: var(--ctl-space-2-5) var(--ctl-space-3-5) var(--ctl-space-0-5);
  }
  .dots{
    display:inline-flex;
    gap: 3px;
    align-items:center;
  }
  .dot{
    width: var(--ctl-space-1-5);
    height: var(--ctl-space-1-5);
    border-radius: var(--ctl-radius-full);
    background: var(--ctl-color-muted);
    opacity: .55;
    animation: d 1.05s ease-in-out infinite;
  }
  .dot:nth-child(2){ animation-delay: .12s; }
  .dot:nth-child(3){ animation-delay: .24s; }
  @keyframes d{
    0%, 100%{ transform: translateY(0); opacity: .45; }
    50%{ transform: translateY(-3px); opacity: .85; }
  }

  /* Overlays */
  .overlay{
    position:absolute;
    inset: 0;
    display:none;
    align-items:center;
    justify-content:center;
    background: color-mix(in srgb, var(--ctl-color-surface) 70%, transparent);
    z-index: 2;
    padding: var(--ctl-space-3-5);
  }
  :host([loading]) .overlay.loading{ display:flex; }
  :host([error]) .overlay.error{ display:flex; background: color-mix(in srgb, var(--ctl-color-surface) 80%, transparent); }

  .overlayCard{
    width: min(520px, 100%);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius-14);
    padding: var(--ctl-space-3);
    box-shadow: var(--ctl-shadow);
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-bold);
    font-size: var(--ctl-font-size-md);
    text-align:center;
  }

  /* Footer slot */
  .footer{
    border-top: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }
  .footer:empty{ display:none; }

  /* Hide templates used for render hooks */
  ::slotted(template[slot="message"]){
    display:none;
  }
</style>

<div class="frame" part="frame">
  <div class="header" part="header"><slot name="header"></slot></div>

  <div class="body" part="body">
    <div class="overlay loading" part="loading" aria-hidden="true">
      <div class="overlayCard">
        <div style="font-weight:900; color: var(--ctl-color-text); margin-bottom: var(--ctl-space-1-5);">Loading…</div>
        <div>Fetching messages.</div>
      </div>
    </div>

    <div class="overlay error" part="error" aria-hidden="true">
      <div class="overlayCard">
        <slot name="error">Something went wrong.</slot>
      </div>
    </div>

    <div class="topControls" part="topControls">
      <button class="loadMoreBtn" type="button" data-role="load-more" hidden>Load older</button>
    </div>

    <div class="typing" part="typing" hidden>
      <span class="dots" aria-hidden="true"><span class="dot"></span><span class="dot"></span><span class="dot"></span></span>
      <span>Typing…</span>
    </div>

    <div class="scroller" part="scroller">
      <ol class="timeline" id="timeline" part="timeline"></ol>

      <div id="empty" style="padding: var(--ctl-space-3) var(--ctl-space-0-5); color: var(--ctl-color-muted); font-weight: var(--ctl-font-weight-bold); text-align:center;" hidden>
        <slot name="empty">No messages yet.</slot>
      </div>

      <slot id="messageTemplateSlot" name="message"></slot>
    </div>
  </div>

  <div class="footer" part="footer"><slot name="footer"></slot></div>
</div>
`;

function toArray(v) { return Array.isArray(v) ? v : []; }
function isObj(v) { return v && typeof v === "object"; }

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function normalizeMessage(m) {
  const msg = isObj(m) ? m : {};
  const id = String(msg.id ?? "");
  const channel = String(msg.channel ?? "");
  const direction = msg.direction === "out" ? "out" : "in";
  const kind = String(msg.kind ?? "text");
  const createdAt = String(msg.createdAt ?? "");
  const subject = msg.subject != null ? String(msg.subject) : "";
  const body = msg.body != null ? String(msg.body) : "";
  const status = msg.status != null ? String(msg.status) : "";
  const author = isObj(msg.author) ? msg.author : {};
  const authorName = String(author.name ?? (direction === "out" ? "You" : "Customer"));
  const authorType = String(author.type ?? (direction === "out" ? "agent" : "customer"));
  return { ...msg, id, channel, direction, kind, createdAt, subject, body, status, author: { ...author, name: authorName, type: authorType } };
}

export class CtlThread extends HTMLElement {
  static get observedAttributes() {
    return ["loading", "error", "typing", "can-load-more", "persona", "channel", "messages", "conversation"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._timeline = this.shadowRoot.getElementById("timeline");
    this._empty = this.shadowRoot.getElementById("empty");
    this._typing = this.shadowRoot.querySelector(".typing");
    this._loadMoreBtn = this.shadowRoot.querySelector('button[data-role="load-more"]');

    this._tmplSlot = this.shadowRoot.getElementById("messageTemplateSlot");
    this._messageTemplate = null;

    this._messages = [];
    this._conversation = null;

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    // Attribute hydration if passed as JSON attrs (optional convenience)
    if (this.hasAttribute("messages")) this.messages = safeJsonParse(this.getAttribute("messages"), []);
    if (this.hasAttribute("conversation")) this.conversation = safeJsonParse(this.getAttribute("conversation"), null);

    this._tmplSlot.addEventListener("slotchange", this._onSlotChange);
    this.shadowRoot.addEventListener("click", this._onClick);

    this._collectTemplate();
    this._sync();
  }

  disconnectedCallback() {
    this._tmplSlot.removeEventListener("slotchange", this._onSlotChange);
    this.shadowRoot.removeEventListener("click", this._onClick);
  }

  attributeChangedCallback(name) {
    if (name === "messages") this.messages = safeJsonParse(this.getAttribute("messages"), []);
    if (name === "conversation") this.conversation = safeJsonParse(this.getAttribute("conversation"), null);
    if (name === "typing" || name === "can-load-more" || name === "loading" || name === "error") this._sync();
  }

  // ---- attrs ----
  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get error() { return this.hasAttribute("error"); }
  set error(v) { v ? this.setAttribute("error", "") : this.removeAttribute("error"); }

  get typing() { return this.hasAttribute("typing"); }
  set typing(v) { v ? this.setAttribute("typing", "") : this.removeAttribute("typing"); }

  get canLoadMore() { return this.hasAttribute("can-load-more"); }
  set canLoadMore(v) { v ? this.setAttribute("can-load-more", "") : this.removeAttribute("can-load-more"); }

  get persona() { return this.getAttribute("persona"); }
  set persona(v) { v == null ? this.removeAttribute("persona") : this.setAttribute("persona", String(v)); }

  get channel() { return this.getAttribute("channel"); }
  set channel(v) { v == null ? this.removeAttribute("channel") : this.setAttribute("channel", String(v)); }

  // ---- props ----
  get messages() { return this._messages; }
  set messages(v) {
    this._messages = toArray(v).map(normalizeMessage).filter((m) => m.id);
    // Optional reflection for consistency with your other components:
    this.setAttribute("messages", JSON.stringify(this._messages));
    this._render();
    this._sync();
  }

  get conversation() { return this._conversation; }
  set conversation(v) {
    this._conversation = isObj(v) ? v : null;
    this.setAttribute("conversation", JSON.stringify(this._conversation));
    // header is slotted, so we don't render it here
  }

  // ---- template override ----
  _onSlotChange() {
    this._collectTemplate();
    this._render();
  }

  _collectTemplate() {
    const assigned = this._tmplSlot.assignedElements({ flatten: true }) || [];
    this._messageTemplate = assigned.find((el) => el.tagName === "TEMPLATE") || null;
  }

  _sync() {
    // Typing visibility
    this._typing.hidden = !this.typing;

    // Load more
    this._loadMoreBtn.hidden = !this.canLoadMore || this.loading || this.error;

    // Empty
    const empty = this._messages.length === 0 && !this.loading && !this.error;
    this._empty.hidden = !empty;
  }

  _render() {
    const msgs = this._messages;

    if (msgs.length === 0) {
      this._timeline.innerHTML = "";
      return;
    }

    const html = msgs.map((m) => {
      const time = fmtTime(m.createdAt);
      const channel = m.channel || (this.channel || "");
      const kind = m.kind || "text";

      // Template override: user controls bubble markup; we still wrap row
      let bubbleInner = "";
      if (this._messageTemplate) {
        bubbleInner = this._renderTemplate(this._messageTemplate, {
          id: m.id,
          channel,
          direction: m.direction,
          kind,
          createdAt: m.createdAt,
          authorName: m.author?.name ?? "",
          authorType: m.author?.type ?? "",
          subject: m.subject ?? "",
          body: m.body ?? "",
          bodyText: String(m.body ?? ""),
          status: m.status ?? ""
        });
      } else {
        // Default renderer
        const subject = m.subject ? `<div style="font-weight:900; margin-bottom: var(--ctl-space-1-5);">${escapeHtml(m.subject)}</div>` : "";
        const bodyHtml = kind === "html"
          ? `<div class="content" data-role="content" data-id="${escapeHtml(m.id)}">${this._sanitizeOrEscapeHtml(m.body)}</div>`
          : `<div class="content" data-role="content" data-id="${escapeHtml(m.id)}">${escapeHtml(m.body)}</div>`;

        bubbleInner = `
          <div class="author">${escapeHtml(m.author?.name ?? "")}</div>
          ${subject}
          ${bodyHtml}
          <div class="footerRow">
            <div class="meta">
              ${channel ? `<span class="pill">${escapeHtml(channel.toUpperCase())}</span>` : ""}
              ${time ? `<span>${escapeHtml(time)}</span>` : ""}
            </div>
            <div class="msgActions" aria-label="Message actions">
              <button class="iconBtn" type="button" data-role="msg-action" data-action="copy" data-id="${escapeHtml(m.id)}" aria-label="Copy message">Copy</button>
              ${m.status === "failed" ? `<button class="iconBtn" type="button" data-role="msg-action" data-action="retry" data-id="${escapeHtml(m.id)}" aria-label="Retry send">Retry</button>` : ""}
            </div>
          </div>
          ${m.status ? `<div class="status">${escapeHtml(m.status)}</div>` : ""}
        `;
      }

      return `
        <li class="row" data-dir="${escapeHtml(m.direction)}" data-kind="${escapeHtml(kind)}" data-id="${escapeHtml(m.id)}">
          <div class="bubble">
            ${bubbleInner}
          </div>
        </li>
      `;
    }).join("");

    this._timeline.innerHTML = html;
  }

  _sanitizeOrEscapeHtml(html) {
    // Enterprise-safe default: escape HTML rather than render it.
    // If you later add a sanitizer, swap this to allow safe tags.
    // For now, treat html kind as "display preformatted-ish content" by escaping.
    return escapeHtml(html);
  }

  _renderTemplate(tmpl, data) {
    // supports {{key}} placeholders in template innerHTML
    const html = tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      return escapeHtml(v == null ? "" : String(v));
    });
    return html;
  }

  // ---- Interaction ----
  _onClick(e) {
    const path = e.composedPath ? e.composedPath() : [];
    const target = e.target;

    // Load more
    const loadBtn = this._findInPath(path, (el) => el?.dataset?.role === "load-more");
    if (loadBtn) {
      this.dispatchEvent(new CustomEvent("ctl-load-more", { bubbles: true, composed: true, detail: {} }));
      return;
    }

    // Message action
    const actionBtn = this._findInPath(path, (el) => el?.dataset?.role === "msg-action");
    if (actionBtn) {
      e.preventDefault();
      const messageId = actionBtn.dataset.id;
      const action = actionBtn.dataset.action || "action";
      const message = this._messages.find((m) => m.id === messageId) || null;

      if (action === "copy" && message) {
        this._copyToClipboard(message.body ?? "");
      }

      this.dispatchEvent(new CustomEvent("ctl-message-action", {
        bubbles: true,
        composed: true,
        detail: { messageId, action, message }
      }));
      return;
    }

    // Link click (router intercept)
    const a = this._findInPath(path, (el) => el?.tagName === "A" && el?.getAttribute?.("href"));
    if (a) {
      const href = a.getAttribute("href");
      // Let normal navigation happen by default, but emit hook for SPA interception.
      this.dispatchEvent(new CustomEvent("ctl-link-click", {
        bubbles: true,
        composed: true,
        detail: { href }
      }));
    }
  }

  _findInPath(path, predicate) {
    for (const el of path) {
      if (predicate(el)) return el;
    }
    return null;
  }

  async _copyToClipboard(text) {
    const t = String(text ?? "");
    try {
      await navigator.clipboard.writeText(t);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = t;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
  }

  // Public helper
  scrollToBottom() {
    const scroller = this.shadowRoot.querySelector(".scroller");
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
  }
}

customElements.define("ctl-thread", CtlThread);
